import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { addClass } from '../api/Classes';
import { useAuth } from '../useAuth';
import { useTimezone } from '../hooks/useTimezone';
import ClassForm, { ClassFormData } from '../components/ClassForm';
import api from '../utils/api';

const CreateClass = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrentDate } = useTimezone();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const search = new URLSearchParams(location.search);
  const memberId = search.get('memberId') || null;
  const returnTo = search.get('returnTo') || '/classes';

  // Extract cycle mongo _id from returnTo if coming from a cycle context
  const cycleId = returnTo.includes('/cycles/')
    ? returnTo.split('/cycles/')[1]?.split('?')[0] || null
    : null;

  const handleSubmit = async (formData: ClassFormData) => {
    setError('');
    setSubmitting(true);

    if (!user) {
      setError('You must be logged in to add a class. Please log in again.');
      setSubmitting(false);
      return;
    }

    try {
      const classData = {
        ...formData,
        classname: formData.classname.trim(),
        classlink: formData.classlink.trim(),
        meetlink: formData.meetlink.trim(),
        memo: formData.memo.trim(),
        docpath: formData.docpath.trim(),
        classwhen: formData.classwhen.trim(),
        classwhere: formData.classwhere.trim(),
        providerno: formData.providerno.trim(),
        programno: formData.programno.trim(),
        input: getCurrentDate(),
      };

      if (memberId) {
        // Team admin mode: create class under the company admin endpoint
        await api.post(`/api/company/members/${memberId}/classes`, {
          ...classData,
          cycleId: cycleId || undefined,
        });
      } else {
        await addClass({ ...classData, userId: user.id } as any);
      }

      navigate(returnTo);
    } catch (err: any) {
      console.error('Failed to add class:', err);
      if (!axios.isAxiosError(err)) {
        setError('An unexpected error occurred. Please try again.');
      } else if (!err.response) {
        setError(`Cannot reach the server at ${API_BASE}. Please ensure the backend is running and try again.`);
      } else if (err.response.status === 401) {
        setError('Your session has expired. Redirecting to login...');
        logout();
        setTimeout(() => navigate('/login'), 1000);
      } else {
        const serverMessage = (err.response.data as any)?.error || (err.response.data as any)?.message;
        setError(serverMessage || `Failed to add class (HTTP ${err.response.status}).`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(returnTo);

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Add New Class</h2>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
          Back
        </button>
      </div>

      {memberId && (
        <div className="alert alert-info py-2 mb-3">
          <strong>Team Admin Mode:</strong> This class will be added to the member's account.
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <ClassForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={submitting}
        showCategoryPanel={false}
        title="Class Information"
      />
    </div>
  );
};

export default CreateClass;
